import React, { useEffect, useMemo } from 'react';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';

import { Greet } from '~/types/models';
import { useRelativeDate } from '~/react-hooks/use-date';
import { useAttributes } from '~/App/Api/Attributes';
import { ViewWebsiteButton } from '../../../Components/RecorderDrawer/Components/ViewWebsiteButton';
import { GREET_RECORD_NOW } from '~/generated/AnalyticsTypes';
import { trackGreetEvent } from '~/App/Services/greet';
import { useQueryString } from '~/react-hooks/use-query-string';
import { openGreetRecorderDrawer } from '../Components/GreetRecorderDrawer';

export type GreetRecordBodyContainerProps = {
  greet: Greet;
};

type ProfileInfoItemProps = {
  label: string;
  value: React.ReactNode;
};

const ProfileInfoItem = ({ label, value }: ProfileInfoItemProps) => {
  return (
    <div className='flex flex-wrap'>
      <span className='w-[140px] flex'>{label}:</span>
      <span className='min-w-[190px] text-neutral-500'>{value}</span>
    </div>
  );
};

const handleRecordClick = (greet: Greet) => {
  trackGreetEvent(GREET_RECORD_NOW, greet);
  openGreetRecorderDrawer(greet);
};

export default function GreetRecordContainer({
  greet,
}: GreetRecordBodyContainerProps) {
  const { profiles } = greet;
  const { openRecorder } = useQueryString<{ openRecorder: boolean }>();

  const profile = profiles[0];
  const isRollUp = profiles.length > 1;
  const assignee = greet.assignee;
  const { status, data: attributes } = useAttributes({
    greetId: greet.id,
    profileId: profile?.id,
  });

  const reputation = useMemo(() => {
    if (profiles.length > 1) return;

    if (status === 'loading') {
      return <i className='far fa-spinner fa-spin' />;
    }

    return (
      attributes?.data.find((attribute) => attribute.name == 'Reputation')
        ?.value ?? 'Unknown'
    );
  }, [attributes, status]);

  useEffect(() => {
    if (openRecorder) {
      openGreetRecorderDrawer(greet);
    }
  }, []);

  return (
    <div className='w-full bg-neutral-50 p-[30px] text-base rounded'>
      <div>
        {!isRollUp && (
          <div className='mb-[15px] flex flex-row flex-wrap gap-x-3'>
            <span className='text-neutral-900'>{profile.email}</span>
            <ViewWebsiteButton email={profile.email} />
          </div>
        )}
      </div>
      <div className='flex flex-col gap-y-[15px] mb-7'>
        {!isRollUp && (
          <>
            {profile.position && (
              <ProfileInfoItem label='Title' value={profile.position} />
            )}
            <ProfileInfoItem label='Reputation' value={reputation} />
          </>
        )}
        <ProfileInfoItem
          label='Added'
          value={useRelativeDate(greet.created_at)}
        />
        {greet.event?.message && (
          <ProfileInfoItem
            label='Task description'
            value={greet.event.message}
          />
        )}
        <div className='flex flex-wrap'>
          <span className='w-[140px] flex'>Assigned to:</span>
          <span className='min-w-[190px] flex gap-x-5'>
            <img
              src={assignee.image}
              width={30}
              height={30}
              className='rounded-full'
            />
            <span className='font-bold'>{assignee.first_name}</span>
          </span>
        </div>
      </div>
      <Button
        className='text-[21px] text-neutral-900 font-bold justify-center w-full hidden md:!flex border-solid'
        variant={ButtonVariant.Primary}
        onClick={() => handleRecordClick(greet)}
      >
        Record
      </Button>
    </div>
  );
}
